"use client"

/**
 * A custom scroll area component built on Radix UI's ScrollArea primitive.
 *
 * When you need the scroll area to fill the remaining viewport (for example under a sticky header),
 * pair it with `useScrollAreaHeight`, which measures the container's distance from the top of the window
 * (`tableTop`) and lets you size the viewport with `calc(100vh - ${tableTop}px)`.
 *
 * How to wire it up:
 * 1. `const { tableTop, containerRef, recalculateTableTop } = useScrollAreaHeight()`.
 * 2. Put `ref={containerRef}` on the element whose top edge should anchor the measurement (usually the flex item that wraps the scroll area).
 * 3. Give `<ScrollArea>` (or its immediate wrapper) a style such as `height: calc(100vh - ${tableTop}px)` so it consumes the remaining space.
 * 4. Call `recalculateTableTop()` after layout shifts (filters opening, tabs mounting, etc.) to keep the height in sync.
 *
 * Example pattern:
 *   const { tableTop, containerRef, recalculateTableTop } = useScrollAreaHeight()
 *   useEffect(() => { recalculateTableTop() }, [recalculateTableTop])
 *   return (
 *     <div ref={containerRef} className="flex-1">
 *       <ScrollArea style={{ height: `calc(100vh - ${tableTop}px)` }}>
 *         ...
 *       </ScrollArea>
 *     </div>
 *   )
 * See `frontend/components/_ui/tab-pages/index.tsx` for a page-level reference, and reuse the optional
 * `viewportRef` prop when you need direct access to the Radix viewport (e.g. for infinite scroll roots).
 *
 * IMPORTANT: This component is designed to work with overflow-hidden.
 * Do not override with overflow-y-auto or overflow-x-auto as this will break the custom scrollbar functionality.
 *
 * Usage:
 * <ScrollArea className="h-[300px]">
 *   Content
 * </ScrollArea>
 *
 * The parent container must have a fixed height or max-height for vertical scrolling to work.
 * If the parent only has max-h but not h-full, it will not work.
 * For horizontal scrolling, a fixed width or max-width is required.
 */
import * as React from "react"
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area"

import { cn } from "../utils"

type ScrollAreaProps = React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
  viewportRef?: React.Ref<HTMLDivElement>
  viewportProps?: React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Viewport>
}

const ScrollArea = React.forwardRef<React.ElementRef<typeof ScrollAreaPrimitive.Root>, ScrollAreaProps>(
  ({ className, children, viewportRef, viewportProps, ...props }, ref) => (
    <ScrollAreaPrimitive.Root ref={ref} className={cn("relative", className, "overflow-hidden")} {...props}>
      <ScrollAreaPrimitive.Viewport
        {...viewportProps}
        ref={viewportRef}
        className={cn("h-full w-full rounded-[inherit] [&>div]:!block", viewportProps?.className)}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  )
)
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = "vertical", ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      "flex touch-none transition-colors select-none",
      orientation === "vertical" && "h-full w-2.5 border-l border-l-transparent p-[1px]",
      orientation === "horizontal" && "h-2.5 flex-col border-t border-t-transparent p-[1px]",
      className
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="bg-secondary relative flex-1 rounded-full" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
))
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName

export { ScrollArea, ScrollBar }
